import { useEffect, useRef } from "react";

/*
  라이브 시청 등록(watch start/ping/stop)을 한 곳에 모은 훅.

  PriorityPreviewCard 안에만 있던 로직을 꺼낸 것이다. 대시보드 「조치가 필요한 위반」
  카드도 같은 등록을 해야 서버가 시청자 수를 제대로 세고, 안 보는 스트림을 정리할 수 있다.
  복사해 두면 한쪽만 고쳐져 반드시 어긋나므로 훅으로 뺐다.

  streamKey 가 비거나 enabled 가 false 면 조용히 정리만 하고 아무것도 켜지 않는다.
*/
const PING_MS = 20000;

function makeId(prefix) {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

async function postWatch(url, body) {
  try {
    await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(body),
      keepalive: true,
    });
  } catch (err) {
    console.error(`watch api 실패: ${url}`, err);
  }
}

export default function useLiveWatch(streamKey, enabled = true, tabPrefix = "focus") {
  const tabIdRef = useRef("");
  const watchIdRef = useRef("");
  const timerRef = useRef(null);
  const activeKeyRef = useRef("");

  if (!tabIdRef.current) {
    // 탭 하나당 한 개. 새로고침해도 같은 값이어야 서버가 중복 시청으로 세지 않는다.
    try {
      const storeKey = `monity-watch-tab-${tabPrefix}`;
      let saved = sessionStorage.getItem(storeKey);
      if (!saved) {
        saved = makeId(tabPrefix);
        sessionStorage.setItem(storeKey, saved);
      }
      tabIdRef.current = saved;
    } catch {
      tabIdRef.current = makeId(tabPrefix);
    }
  }
  if (!watchIdRef.current) watchIdRef.current = makeId(`${tabPrefix}-w`);

  useEffect(() => {
    const key = String(streamKey || "").trim();
    const prev = activeKeyRef.current;

    const clearPing = () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };

    const stop = (target) => {
      clearPing();
      if (!target) return;
      postWatch("/api/live/watch/stop", {
        streamKey: target,
        tabId: tabIdRef.current,
        watchId: watchIdRef.current,
      });
    };

    if (prev && prev !== key) stop(prev);

    if (!enabled || !key) {
      activeKeyRef.current = "";
      clearPing();
      return undefined;
    }

    activeKeyRef.current = key;

    postWatch("/api/live/watch/start", {
      streamKey: key,
      tabId: tabIdRef.current,
      watchId: watchIdRef.current,
    });

    clearPing();
    timerRef.current = setInterval(() => {
      postWatch("/api/live/watch/ping", {
        streamKey: key,
        tabId: tabIdRef.current,
        watchId: watchIdRef.current,
      });
    }, PING_MS);

    return () => {
      activeKeyRef.current = "";
      stop(key);
    };
  }, [streamKey, enabled, tabPrefix]);
}
